import { useState, useMemo } from 'react';
import {
  X,
  MessageCircle,
  Mail,
  Copy,
  ExternalLink,
  Check,
  Send,
  Sparkles,
  UserCheck,
  BookOpen,
  AlertCircle,
  FileCheck2,
  HeartHandshake,
  KeyRound,
} from 'lucide-react';
import { Button } from '@lms/ui';
import { useNotificationStore } from '../store/notifications';
import { useLanguage } from '../lib/LanguageContext';

export interface CommunicationRecipient {
  id: string;
  name: string;
  phone?: string;
  email: string;
  gender?: 'MALE' | 'FEMALE';
  programName?: string;
  domisili?: string;
  participantCode?: string;
}

export type ChannelType = 'whatsapp' | 'email';

interface MessageTemplate {
  id: string;
  title: { id: string; ar: string };
  category: 'REGISTRATION' | 'EVALUATION' | 'ACADEMIC' | 'VERIFICATION' | 'GREETING';
  icon: typeof MessageCircle;
  subject?: string;
  body: (data: CommunicationRecipient) => string;
}

const TEMPLATES: MessageTemplate[] = [
  {
    id: 'registration_confirm',
    title: { id: 'Konfirmasi Pendaftaran Program', ar: 'تأكيد التسجيل في البرنامج' },
    category: 'REGISTRATION',
    icon: FileCheck2,
    subject: 'Konfirmasi Pendaftaran Program — Tarbiyah Sunnah Learning',
    body: data => {
      const panggilan = data.gender === 'FEMALE' ? 'Ukhti' : 'Akhi';
      const prog = data.programName || 'Program Pembelajaran';
      return `Assalamu'alaikum Warahmatullahi Wabarakatuh,\n\nSemoga ${panggilan} ${data.name} senantiasa dalam limpahan taufik dan hidayah Allah Ta'ala.\n\nKami mengonfirmasi bahwa pendaftaran Anda pada *${prog}* telah kami terima di sistem Tarbiyah Sunnah Learning.\n\nSilakan masuk ke portal peserta di https://my.tarbiyahsunnahlearning.or.id untuk memeriksa status dan petunjuk belajar berikutnya.\n\nJazakumullahu khairan wa barakallahu fiikum.\n\nSalam Ta'dzim,\n*Tim Administrasi Tarbiyah Sunnah Learning*`;
    },
  },
  {
    id: 'evaluation_reminder',
    title: { id: 'Pengingat Evaluasi / Ujian', ar: 'تذكير بالاختبار / التقييم' },
    category: 'EVALUATION',
    icon: AlertCircle,
    subject: 'Pengingat Batas Waktu Evaluasi — Tarbiyah Sunnah Learning',
    body: data => {
      const panggilan = data.gender === 'FEMALE' ? 'Ukhti' : 'Akhi';
      const prog = data.programName || 'Program Pembelajaran';
      return `Assalamu'alaikum Warahmatullahi Wabarakatuh,\n\nAhlan wa sahlan ${panggilan} ${data.name},\n\nKami mengingatkan bahwa terdapat evaluasi/ujian materi pada *${prog}* yang perlu dikerjakan sebelum batas waktu yang ditentukan.\n\nMohon luangkan waktu untuk menyelesaikan evaluasi melalui portal peserta: https://my.tarbiyahsunnahlearning.or.id\n\nSemoga Allah mudahkan seluruh ikhtiar menuntut ilmu antum/antunna.\n\nBaarakallahu Fiikum,\n*Tim Akademik Tarbiyah Sunnah Learning*`;
    },
  },
  {
    id: 'academic_update',
    title: { id: 'Pengumuman / Jadwal Pembelajaran', ar: 'إعلان / جدول تعليمي' },
    category: 'ACADEMIC',
    icon: BookOpen,
    subject: 'Informasi Perkuliahan & Pembelajaran — Tarbiyah Sunnah Learning',
    body: data => {
      const panggilan = data.gender === 'FEMALE' ? 'Ukhti' : 'Akhi';
      const prog = data.programName || 'Program Pembelajaran';
      return `Assalamu'alaikum Warahmatullahi Wabarakatuh,\n\nKepada ${panggilan} ${data.name},\n\nTerdapat informasi dan pembaruan materi terkini pada *${prog}*.\n\nSilakan cek jadwal sesi pembelajaran dan materi panduan di portal peserta: https://my.tarbiyahsunnahlearning.or.id\n\nApabila ada pertanyaan seputar materi, silakan hubungi fasilitator kelas masing-masing.\n\nWassalamu'alaikum Warahmatullahi Wabarakatuh,\n*Admin Tarbiyah Sunnah Learning*`;
    },
  },
  {
    id: 'profile_verification',
    title: { id: 'Permintaan Kelengkapan Data Profil', ar: 'طلب استكمال بيانات الملف الشخصي' },
    category: 'VERIFICATION',
    icon: UserCheck,
    subject: 'Permintaan Kelengkapan Data Profil Peserta — TSL',
    body: data => {
      const panggilan = data.gender === 'FEMALE' ? 'Ukhti' : 'Akhi';
      return `Assalamu'alaikum Warahmatullahi Wabarakatuh,\n\nSemoga ${panggilan} ${data.name} dalam keadaan sehat wal 'afiat.\n\nDalam rangka tertib administrasi akademik dan penerbitan nomor induk peserta, kami memohon kesediaan antum/antunna untuk melengkapi data profil (seperti nomor WhatsApp aktif, kota/kabupaten domisili, dan jenis kelamin) di portal peserta: https://my.tarbiyahsunnahlearning.or.id/profile\n\nTerima kasih atas kerja sama dan perhatiannya.\n\nJazaakumullahu Khayran,\n*Tim Administrasi Tarbiyah Sunnah Learning*`;
    },
  },
  {
    id: 'password_reset_sent',
    title: { id: 'Instruksi Reset Kata Sandi', ar: 'تعليمات إعادة تعيين كلمة المرور' },
    category: 'VERIFICATION',
    icon: KeyRound,
    subject: 'Instruksi Reset Kata Sandi — Tarbiyah Sunnah Learning',
    body: data => {
      const panggilan = data.gender === 'FEMALE' ? 'Ukhti' : 'Akhi';
      return `Assalamu'alaikum Warahmatullahi Wabarakatuh,\n\n${panggilan} ${data.name},\n\nKami telah mengirimkan tautan reset kata sandi untuk akun Anda ke email ${data.email}. Silakan buka inbox atau folder spam, lalu gunakan tautan tersebut untuk membuat kata sandi baru.\n\nDemi keamanan akun, tautan reset tidak dikirimkan melalui WhatsApp dan tidak perlu membagikan kata sandi kepada siapa pun.\n\nJika email belum diterima, silakan hubungi admin.\n\nJazakumullahu khairan.\n\n*Tim Administrasi Tarbiyah Sunnah Learning*`;
    },
  },
  {
    id: 'greeting_mentorship',
    title: { id: 'Sapaan Ukhuwah & Pembinaan Peserta', ar: 'تحية أخوية ومتابعة دراسية' },
    category: 'GREETING',
    icon: HeartHandshake,
    subject: 'Sapaan Ukhuwah & Motivasi Belajar — Tarbiyah Sunnah Learning',
    body: data => {
      const panggilan = data.gender === 'FEMALE' ? 'Ukhti' : 'Akhi';
      return `Assalamu'alaikum Warahmatullahi Wabarakatuh,\n\nAhlan wa sahlan ${panggilan} ${data.name},\n\nSemoga senantiasa istiqomah dalam menuntut ilmu syar'i bersama Tarbiyah Sunnah Learning.\n\nJika ada kendala dalam proses belajar atau membutuhkan bantuan akses portal, tim kami siap membantu antum/antunna.\n\nSemoga ilmu yang dipelajari menjadi ilmu yang bermanfaat dan berkah.\n\nSalam Ukhuwah,\n*Tim Tarbiyah Sunnah Learning*`;
    },
  },
];

interface CommunicationModalProps {
  recipient: CommunicationRecipient;
  initialChannel?: ChannelType;
  initialTemplateId?: string;
  onClose: () => void;
}

export function CommunicationModal({
  recipient,
  initialChannel = 'whatsapp',
  initialTemplateId,
  onClose,
}: CommunicationModalProps) {
  const { language } = useLanguage();
  const { addNotification } = useNotificationStore();
  const initialTemplate =
    TEMPLATES.find(template => template.id === initialTemplateId) ?? TEMPLATES[0];

  const [channel, setChannel] = useState<ChannelType>(initialChannel);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(initialTemplate.id);
  const [subject, setSubject] = useState<string>(initialTemplate.subject || '');
  const [message, setMessage] = useState<string>(() => initialTemplate.body(recipient));
  const [copied, setCopied] = useState(false);

  // Clean phone number for WhatsApp: format to international 62
  const cleanedPhone = useMemo(() => {
    if (!recipient.phone) return '';
    let p = recipient.phone.replace(/[^0-9]/g, '');
    if (p.startsWith('0')) {
      p = '62' + p.substring(1);
    } else if (p.startsWith('+62')) {
      p = '62' + p.substring(3);
    } else if (!p.startsWith('62')) {
      p = '62' + p;
    }
    return p;
  }, [recipient.phone]);

  const handleSelectTemplate = (tmpl: MessageTemplate) => {
    setSelectedTemplateId(tmpl.id);
    setSubject(tmpl.subject || '');
    setMessage(tmpl.body(recipient));
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      addNotification({
        type: 'success',
        title: 'Teks pesan disalin',
        message: 'Template komunikasi berhasil disalin ke clipboard.',
      });
    } catch {
      // ignore
    }
  };

  const handleSendWhatsApp = () => {
    if (!cleanedPhone) {
      addNotification({
        type: 'error',
        title: 'Nomor telepon tidak tersedia',
        message: 'Peserta belum memiliki nomor telepon/WhatsApp yang tersimpan.',
      });
      return;
    }
    const encoded = encodeURIComponent(message);
    const url = `https://wa.me/${cleanedPhone}?text=${encoded}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSendEmail = () => {
    if (!recipient.email) {
      addNotification({
        type: 'error',
        title: 'Email tidak tersedia',
        message: 'Peserta belum memiliki alamat email yang tersimpan.',
      });
      return;
    }
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(message);
    const mailto = `mailto:${recipient.email}?subject=${encodedSubject}&body=${encodedBody}`;
    window.location.href = mailto;
  };

  return (
    <div className="fixed inset-0 bg-on-surface/40 backdrop-blur-sm z-[70] flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-surface-container-lowest w-full max-w-2xl rounded-3xl shadow-level4 border border-outline-variant/10 overflow-hidden flex flex-col max-h-[90vh] animate-scaleUp">
        {/* Header */}
        <div className="px-6 py-5 border-b border-outline-variant/10 bg-surface-container-low flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
              {channel === 'whatsapp' ? <MessageCircle size={22} /> : <Mail size={22} />}
            </div>
            <div>
              <h3 className="text-lg font-headline font-bold text-on-surface m-0 flex items-center gap-2">
                Hubungi Peserta
                <span className="text-xs px-2 py-0.5 rounded-full bg-surface-container-high font-body font-semibold text-on-surface-variant">
                  {recipient.name}
                </span>
              </h3>
              <p className="text-xs font-body text-on-surface-variant m-0 mt-0.5">
                Pilih template pesan resmi untuk komunikasi terstandar via WhatsApp / Email.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-on-surface-variant hover:text-primary hover:bg-surface-container-high rounded-xl transition-all"
          >
            <X size={20} />
          </button>
        </div>

        {/* Channel & Recipient Summary Bar */}
        <div className="px-6 py-3 bg-surface-container-low/40 border-b border-outline-variant/10 flex flex-wrap items-center justify-between gap-3">
          {/* Channel Tabs */}
          <div className="flex items-center bg-surface-container-high rounded-xl p-1 gap-1">
            <button
              type="button"
              onClick={() => setChannel('whatsapp')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                channel === 'whatsapp'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              <MessageCircle size={14} />
              WhatsApp {recipient.phone ? `(${recipient.phone})` : '(Kosong)'}
            </button>
            <button
              type="button"
              onClick={() => setChannel('email')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                channel === 'email'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              <Mail size={14} />
              Email ({recipient.email})
            </button>
          </div>

          {/* Gender & Program Badge */}
          <div className="flex items-center gap-2 text-xs">
            {recipient.gender && (
              <span
                className={`px-2 py-0.5 rounded-md font-bold text-[10px] ${
                  recipient.gender === 'FEMALE'
                    ? 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300'
                    : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                }`}
              >
                {recipient.gender === 'FEMALE' ? 'Akhwat' : 'Ikhwan'}
              </span>
            )}
            {recipient.programName && (
              <span className="px-2 py-0.5 rounded-md bg-surface-container-high text-on-surface-variant font-medium text-[10px] truncate max-w-[160px]">
                {recipient.programName}
              </span>
            )}
          </div>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
          {/* Template Selector Carousel / Grid */}
          <div className="space-y-2">
            <label className="text-xs font-label font-bold text-on-surface flex items-center gap-1.5">
              <Sparkles size={14} className="text-primary" />
              Pilih Template Komunikasi
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {TEMPLATES.map(tmpl => {
                const IconComponent = tmpl.icon;
                const isSelected = selectedTemplateId === tmpl.id;
                return (
                  <button
                    key={tmpl.id}
                    type="button"
                    onClick={() => handleSelectTemplate(tmpl)}
                    className={`flex items-center gap-3 p-3 rounded-xl text-left border transition-all ${
                      isSelected
                        ? 'border-primary bg-primary/5 shadow-sm text-on-surface'
                        : 'border-outline-variant/10 bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high'
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        isSelected
                          ? 'bg-primary text-white'
                          : 'bg-surface-container-high text-on-surface-variant'
                      }`}
                    >
                      <IconComponent size={16} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-bold truncate">
                        {language === 'ar' ? tmpl.title.ar : tmpl.title.id}
                      </div>
                      <div className="text-[10px] opacity-70 uppercase font-semibold">
                        {tmpl.category}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Email Subject Field (Only when in Email mode) */}
          {channel === 'email' && (
            <div className="space-y-1.5">
              <label className="text-xs font-label font-bold text-on-surface">Subjek Email</label>
              <input
                type="text"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                className="w-full bg-surface-container-low border-none rounded-xl px-4 py-2.5 text-xs font-body text-on-surface outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          )}

          {/* Message Textarea */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-label font-bold text-on-surface">
                Isi Pesan (Dapat Disesuaikan)
              </label>
              <span className="text-[10px] text-on-surface-variant opacity-60">
                {message.length} karakter
              </span>
            </div>
            <textarea
              rows={8}
              value={message}
              onChange={e => setMessage(e.target.value)}
              className="w-full bg-surface-container-low border-none rounded-2xl p-4 text-xs font-body text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition-all resize-y leading-relaxed font-sans"
            />
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 bg-surface-container-low border-t border-outline-variant/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            leftIcon={copied ? <Check size={14} className="text-primary" /> : <Copy size={14} />}
            className="text-xs font-semibold w-full sm:w-auto"
          >
            {copied ? 'Tersalin ke Clipboard!' : 'Salin Pesan'}
          </Button>

          <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              className="text-xs font-semibold"
            >
              Tutup
            </Button>

            {channel === 'whatsapp' ? (
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={handleSendWhatsApp}
                disabled={!cleanedPhone}
                leftIcon={<Send size={14} />}
                rightIcon={<ExternalLink size={12} />}
                className="text-xs font-bold px-5 bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-level1"
              >
                Kirim via WhatsApp (wa.me)
              </Button>
            ) : (
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={handleSendEmail}
                disabled={!recipient.email}
                leftIcon={<Mail size={14} />}
                rightIcon={<ExternalLink size={12} />}
                className="text-xs font-bold px-5 shadow-level1"
              >
                Kirim via Email Client
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
